import { ColumnVisibilityItem } from "providers/Auth";
import { LeadView } from "./constants";

export enum CreatedWithinOption {
  None = "none",
  Last7Days = "7",
  Last30Days = "30",
  Custom = "custom"
}

export enum ShowingWithinOption {
  None = "none",
  Last7Days = "7",
  Last30Days = "30",
  Next7Days = "next_7",
  Next30Days = "next_30",
  Custom = "custom"
}

export interface LeadsFilters {
  properties?: { id: string; name: string }[];
  stages?: { id: string; name: string }[];
  leadSources?: { id: string; name: string }[];
  leadTypes?: string[];
  activityTypes?: string[];
  /** Keep only leads that came in through a waitlisted unit. */
  onWaitlist?: boolean;
  createdWithin?: CreatedWithinOption;
  createdAfter?: string;
  createdBefore?: string;
  showingWithin?: ShowingWithinOption;
  showingAfter?: string;
  showingBefore?: string;
}

/** The multi-select filters, i.e. the ones a filter badge can remove one entry from. */
export type RemovableFilterKey =
  | "properties"
  | "stages"
  | "leadSources"
  | "leadTypes"
  | "activityTypes";

/**
 * Frontend-owned snapshot of the Leads filter state, persisted verbatim by the
 * backend as an opaque JSON blob. Keys mirror the snake_case Leads index
 * query-param vocabulary so the payload round-trips losslessly. The structured
 * `{ id, name }[]` values (rather than id-only) are kept so applying a saved
 * view can re-render the filter badges without an extra lookup.
 */
export interface LeadViewFilters {
  search?: string;
  starred?: boolean;
  // Which Leads layout this view was saved in. Omitted (treated as List) for
  // views saved before this existed, so applying them keeps the legacy List
  // behavior.
  view?: LeadView;
  properties?: { id: string; name: string }[];
  stages?: { id: string; name: string }[];
  lead_sources?: { id: string; name: string }[];
  activity_types?: string[];
  lead_types?: string[];
  from_waitlist?: boolean;
  created_within?: CreatedWithinOption;
  created_after?: string;
  created_before?: string;
  showing_within?: ShowingWithinOption;
  showing_after?: string;
  showing_before?: string;
  assignee_ids?: number[];
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  pipeline_stage_order?: string[];
}

/**
 * Frontend-owned snapshot of the Leads column-visibility config, keyed by the
 * column-visibility tab key (e.g. "leads"), persisted verbatim by the backend.
 */
export type LeadViewColumns = Record<string, ColumnVisibilityItem[]>;

/** camelCase model exposed to React over the snake_case saved-view API. */
export interface LeadSavedView {
  id: number;
  name: string;
  filters: LeadViewFilters;
  columns: LeadViewColumns;
  createdAt: string;
  updatedAt: string;
}
